/**
 * <g-kpi> - KPI Card
 *
 * Self-contained dashboard tile. Either receives a precomputed `.value` from
 * the host, OR receives `.records` + a `.config` (metric + field + predicate)
 * and aggregates internally — so the host's Alpine code never reimplements
 * sum/count/percent.
 *
 * @example  Precomputed:
 *   <g-kpi label="Total Outstanding" render="currency" currency="USD"
 *          icon="circle-dollar" color="error" sublabel="Remaining"></g-kpi>
 *   <script>k.value = 1250000;</script>
 *
 * @example  Self-aggregating:
 *   <g-kpi label="Active Accounts" render="count" icon="circle-check" color="success"></g-kpi>
 *   <script>
 *     k.config = { metric: 'count', predicate: { field: 'Status', op: { eq: 'Active' } } };
 *     k.records = accounts;          // fires re-render
 *   </script>
 *
 * @example  Filtered sum (predicate works on every metric, not just count):
 *   <g-kpi label="Open Pipeline" render="currency" currency="USD"></g-kpi>
 *   <script>
 *     k.config = {
 *       metric: 'sum', field: 'Amount',
 *       predicate: { field: 'Stage', op: { in: ['Discovery','Proposal','Negotiation'] } }
 *     };
 *     k.records = deals;
 *   </script>
 *
 * Attributes:
 *   label       string (required)
 *   render      'currency' | 'number' | 'percent' | 'count'  (default 'number')
 *   currency    ISO code (default 'USD') — only used when render='currency'
 *   icon        Font Awesome name (without 'fa-' prefix), e.g. 'circle-dollar'
 *   icon-style  'regular' | 'solid' | 'light' (default 'regular')
 *   color       DaisyUI semantic: primary | secondary | accent | success | warning | error | info | neutral (default 'primary')
 *   sublabel    small context line under the value
 *   loading     boolean attr — show shimmer
 *   source      same-origin endpoint, e.g. "/api/deals" — the component loads
 *               `.records` itself from the shared collection (src/data/collection.js)
 *               and re-aggregates on every change. `.records` set by hand is ignored
 *               while `source` is set.
 *   live        socket entity name, e.g. "deal" — follow `deal:created|updated|deleted`
 *
 * Properties:
 *   .value      number — precomputed; takes precedence over self-aggregation
 *   .records    array — data to aggregate from (used with .config)
 *   .config     { metric, field, predicate?, numerator?, denominator? }
 */
import { aggregate } from '../util/aggregate.js';
import { SourceBinding } from '../data/collection.js';

const ALLOWED_COLORS = new Set([
  'primary', 'secondary', 'accent', 'success', 'warning', 'error', 'info', 'neutral',
]);

export class GKpi extends HTMLElement {
  constructor() {
    super();
    this._value = null;
    this._records = null;
    this._config = null;
    this._renderScheduled = false;
    this._source = new SourceBinding(this, (c) => {
      this._records = c.records;
      this._sourceLoading = c.status === 'loading' && c.records.length === 0;
      this.scheduleRender();
    });
    this._sourceLoading = false;
  }

  static get observedAttributes() {
    return ['label', 'render', 'currency', 'icon', 'icon-style', 'color', 'sublabel', 'loading', 'source', 'live'];
  }

  attributeChangedCallback(name) {
    if ((name === 'source' || name === 'live') && this.isConnected) this._source.sync();
    this.scheduleRender();
  }

  disconnectedCallback() { this._source.stop(); }

  connectedCallback() {
    // h-full so the host stretches to fill its grid/flex cell — combined with
    // h-full on the inner card below, every KPI in a row ends up the same
    // height regardless of whether `sublabel` is set. Without this, cards
    // without sublabels are visibly shorter (see g-kpi visual regression).
    this.classList.add('block', 'h-full');
    if (!this._root) {
      this._root = document.createElement('div');
      this._root.className = 'h-full';
      this.appendChild(this._root);
    }
    this._source.sync();
    this.scheduleRender();
  }

  // ── Public API ──

  get value() { return this._value; }
  set value(v) { this._value = v; this.scheduleRender(); }

  get records() { return this._records; }
  set records(rs) {
    if (this._source.active) return; // the source owns the records
    this._records = Array.isArray(rs) ? rs : null;
    this.scheduleRender();
  }

  get config() { return this._config; }
  set config(c) { this._config = c && typeof c === 'object' ? c : null; this.scheduleRender(); }

  // ── Internals ──

  scheduleRender() {
    if (this._renderScheduled) return;
    this._renderScheduled = true;
    requestAnimationFrame(() => {
      this._renderScheduled = false;
      this._render();
    });
  }

  _resolveValue() {
    // Precomputed wins
    if (this._value != null) return this._value;
    if (this._records && this._config) return aggregate(this._records, this._config);
    return null;
  }

  _format(v) {
    if (v == null || (typeof v === 'number' && isNaN(v))) return '—';
    const render = (this.getAttribute('render') || 'number').toLowerCase();
    switch (render) {
      case 'currency': {
        const n = typeof v === 'number' ? v : parseFloat(v);
        if (isNaN(n)) return '—';
        try {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: this.getAttribute('currency') || 'USD',
            maximumFractionDigits: 0,
          }).format(n);
        } catch { return String(v); }
      }
      case 'percent': {
        const n = typeof v === 'number' ? v : parseFloat(v);
        if (isNaN(n)) return '—';
        return (n * 100).toFixed(1) + '%';
      }
      case 'count':
      case 'number':
      default: {
        const n = typeof v === 'number' ? v : parseFloat(v);
        if (isNaN(n)) return String(v);
        return new Intl.NumberFormat('en-US').format(n);
      }
    }
  }

  _render() {
    if (!this._root) return;
    const label = this.getAttribute('label') || '';
    const sublabel = this.getAttribute('sublabel') || '';
    const iconName = this.getAttribute('icon');
    const iconStyle = this.getAttribute('icon-style') || 'regular';
    const colorAttr = this.getAttribute('color');
    const color = ALLOWED_COLORS.has(colorAttr) ? colorAttr : 'primary';
    const loading = this.hasAttribute('loading') || this._sourceLoading;
    const valueText = loading ? '' : this._format(this._resolveValue());

    const iconHtml = iconName
      ? `<div class="rounded-lg bg-${color}/10 text-${color} w-10 h-10 flex items-center justify-center shrink-0">
           <i class="fa-${iconStyle} fa-${escapeHtml(iconName)} text-lg"></i>
         </div>`
      : '';

    const valueHtml = loading
      ? `<div class="skeleton h-7 w-24 mt-1"></div>`
      : `<div class="text-2xl font-bold leading-tight" style="color: oklch(var(--bc));">${escapeHtml(valueText)}</div>`;

    const sublabelHtml = sublabel
      ? `<div class="text-xs text-base-content/60 mt-1 truncate">${escapeHtml(sublabel)}</div>`
      : '';

    this._root.innerHTML = `
      <div class="card bg-base-100 shadow-sm h-full">
        <div class="card-body p-4 flex flex-row items-start gap-3">
          ${iconHtml}
          <div class="min-w-0 flex-1">
            <div class="text-xs uppercase tracking-wider text-base-content/60 truncate">${escapeHtml(label)}</div>
            ${valueHtml}
            ${sublabelHtml}
          </div>
        </div>
      </div>
    `;
  }
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

if (!customElements.get('g-kpi')) {
  customElements.define('g-kpi', GKpi);
}
